import React, { useId, useState } from 'react';
import { Alert, ButtonGroup, Col, Form, InputGroup, Row, ToggleButton } from 'react-bootstrap';
import { buildAddEnemyCommand, EnemyInputError, EnemyStats, parseFultimatorEnemy } from '@common/FabulaUltimaEnemy';
import { fuText, FuLocale } from '../../i18n/fuLocale';
import './AddEnemyForm.css';

const labels = {
  zh: {
    manual: '手动填写', json: '导入 JSON', name: '敌人名字', method: '添加方式',
    maxHp: '最大 HP', maxMp: '最大 MP', dex: '敏捷 DEX', ins: '洞察 INS', mig: '力量 MIG', wlp: '意志 WLP',
    def: '物防加值', mDef: '魔防加值', fixedDef: '使用固定物理防御', fixedValue: '固定物防值',
    defHint: '物防 = 敏捷 + 加值', fixedHint: '直接作为物防，通常为 8–15',
    hint: '满 HP / MP · 未研究', importHint: '粘贴 Fultimator 导出的单个 NPC，自动计算 HP / MP。',
    edit: '填入表单继续编辑', submit: '添加敌人', namePlaceholder: '给这位敌人起个名字',
    jsonError: 'JSON 无效，请粘贴含 name、lvl 和 attributes 的单个 NPC 对象。',
    nameError: '请填写名字；名字不能包含反引号或换行。', duplicate: '这个名字已经存在，请更换敌人名字。',
    integer: '请填写有效整数，HP 至少为 1，MP 至少为 0。',
    dice: '属性骰只能选择 6、8、10 或 12。', rank: '不支持此阶级，请使用 minion、elite、champion2 或 champion3。',
  },
  en: {
    manual: 'Enter stats', json: 'Import JSON', name: 'Enemy name', method: 'Input method',
    maxHp: 'Max HP', maxMp: 'Max MP', dex: 'DEX', ins: 'INS', mig: 'MIG', wlp: 'WLP',
    def: 'Defense modifier', mDef: 'Magic defense modifier', fixedDef: 'Use fixed physical defense', fixedValue: 'Fixed defense value',
    defHint: 'Defense = DEX + modifier', fixedHint: 'Used as Defense, usually 8–15',
    hint: 'Full HP / MP · Unstudied', importHint: 'Paste one NPC exported from Fultimator. HP / MP are calculated automatically.',
    edit: 'Load into form to edit', submit: 'Add enemy', namePlaceholder: 'Name your enemy',
    jsonError: 'Invalid JSON. Paste one NPC object with name, lvl and attributes.',
    nameError: 'Enter a name without backticks or line breaks.', duplicate: 'This name already exists. Choose another enemy name.',
    integer: 'Enter valid integers. HP must be at least 1 and MP at least 0.',
    dice: 'Attribute dice must be 6, 8, 10 or 12.', rank: 'Unsupported rank. Use minion, elite, champion2 or champion3.',
  },
};

type NumericField = Exclude<keyof EnemyStats, 'name' | 'fixedDef'>;
const defaults = { name: '', maxHp: '50', maxMp: '50', dex: '8', ins: '8', mig: '8', wlp: '8', def: '0', mDef: '0', fixedDef: false };

export function AddEnemyForm({ onCommands, existingNames, canEdit, isHost, locale }: {
  onCommands: (commands: string[]) => void;
  existingNames: readonly string[];
  canEdit: boolean;
  isHost: boolean;
  locale: FuLocale;
}) {
  const id = useId();
  const t = labels[locale];
  const [mode, setMode] = useState('manual');
  const [form, setForm] = useState(defaults);
  const [json, setJson] = useState('');
  const [error, setError] = useState<EnemyInputError | null>(null);
  const [sent, setSent] = useState('');

  const readEnemy = (): EnemyStats => mode === 'json' ? parseFultimatorEnemy(json) : {
    ...form,
    // Keep empty/invalid number inputs invalid rather than silently coercing them to zero.
    maxHp: form.maxHp.trim() ? Number(form.maxHp) : NaN,
    maxMp: form.maxMp.trim() ? Number(form.maxMp) : NaN,
    dex: Number(form.dex), ins: Number(form.ins), mig: Number(form.mig), wlp: Number(form.wlp),
    def: form.def.trim() ? Number(form.def) : NaN,
    mDef: form.mDef.trim() ? Number(form.mDef) : NaN,
  };

  const reportError = (cause: unknown) => setError(cause instanceof EnemyInputError ? cause : new EnemyInputError('json'));
  const numericInput = (field: NumericField, dice = false) => (
    <Col xs={dice ? 3 : 6} key={field}>
      <Form.Group controlId={`${id}-${field}`} className={`enemy-stat${field === 'maxHp' ? ' enemy-stat--hp' : field === 'maxMp' ? ' enemy-stat--mp' : ''}`}>
        <Form.Label>{t[field]}</Form.Label>
        {dice ? (
          <Form.Select value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}>
            {[6, 8, 10, 12].map(value => <option key={value} value={value}>d{value}</option>)}
          </Form.Select>
        ) : (
          <Form.Control type="number" step={1} min={field === 'maxHp' ? 1 : field === 'maxMp' ? 0 : undefined}
            max={2147483647} value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })} />
        )}
      </Form.Group>
    </Col>
  );

  return (
    <Form className="add-enemy-form" aria-label={t.submit} noValidate onSubmit={e => {
      e.preventDefault();
      if (!canEdit) return;
      try {
        const enemy = readEnemy();
        const command = buildAddEnemyCommand(enemy, existingNames);
        onCommands([command]);
        setError(null);
        setSent(enemy.name);
        if (mode === 'json') setJson('');
        else setForm({ ...form, name: '' });
      } catch (cause) { reportError(cause); }
    }} onChange={() => { setError(null); setSent(''); }}>
      <ButtonGroup className="enemy-input-modes" aria-label={t.method}>
        {(['manual', 'json'] as const).map(value => <ToggleButton key={value} id={`${id}-${value}-mode`}
          type="radio" name={`${id}-mode`} value={value} variant="light" checked={mode === value}
          onChange={() => { setMode(value); setError(null); setSent(''); }}>{t[value]}</ToggleButton>)}
      </ButtonGroup>
      <fieldset disabled={!canEdit}>
        <div className="enemy-form-body">
        {mode === 'json' ? <>
          <Form.Group controlId={`${id}-json`} className="enemy-json">
            <Form.Label>Fultimator JSON</Form.Label>
            <Form.Control as="textarea" rows={7} spellCheck={false} placeholder={'{\n  "name": "Goblin",\n  "lvl": 5,\n  "attributes": { … }\n}'} value={json} onChange={e => setJson(e.target.value)} />
            <Form.Text>{t.importHint}</Form.Text>
          </Form.Group>
          <button type="button" className="btn btn-link btn-sm enemy-load-json" onClick={() => {
            try {
              const enemy = parseFultimatorEnemy(json);
              buildAddEnemyCommand(enemy);
              setForm({
                ...enemy, maxHp: String(enemy.maxHp), maxMp: String(enemy.maxMp),
                dex: String(enemy.dex), ins: String(enemy.ins), mig: String(enemy.mig), wlp: String(enemy.wlp),
                def: String(enemy.def), mDef: String(enemy.mDef),
              });
              setMode('manual'); setError(null); setSent('');
            } catch (cause) { reportError(cause); }
          }}>{t.edit}</button>
        </> : <>
          <Form.Group controlId={`${id}-name`} className="enemy-name-field">
            <Form.Label>{t.name}</Form.Label>
            <Form.Control placeholder={t.namePlaceholder} autoComplete="off" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </Form.Group>
          <Row className="g-2 enemy-resources">{numericInput('maxHp')}{numericInput('maxMp')}</Row>
          <Row className="g-2 enemy-attributes">{(['dex', 'ins', 'mig', 'wlp'] as const).map(field => numericInput(field, true))}</Row>
          <Row className="g-2 enemy-defenses">
            <Col xs={6}>
              <Form.Group controlId={`${id}-def`} className="enemy-stat">
                <Form.Label>{form.fixedDef ? t.fixedValue : t.def}</Form.Label>
                <InputGroup className="enemy-defense-value">
                  {!form.fixedDef && !form.def.startsWith('-') && <InputGroup.Text aria-hidden="true">+</InputGroup.Text>}
                  <Form.Control type="number" step={1} max={2147483647} value={form.def}
                    aria-describedby={`${id}-def-hint`} placeholder={form.fixedDef ? '8–15' : '1 / 3 / 5'}
                    onChange={e => setForm({ ...form, def: e.target.value })} />
                </InputGroup>
                <Form.Text id={`${id}-def-hint`} className="enemy-defense-hint">{form.fixedDef ? t.fixedHint : t.defHint}</Form.Text>
                <Form.Check id={`${id}-fixed`} className="enemy-fixed-defense" label={t.fixedDef} checked={form.fixedDef}
                  onChange={e => setForm({ ...form, fixedDef: e.target.checked })} />
              </Form.Group>
            </Col>
            {numericInput('mDef')}
          </Row>
        </>}
        </div>
        <div className="enemy-form-footer">
          <span>{t.hint}</span>
          <button type="submit" className="btn btn-primary enemy-submit"><span aria-hidden="true">＋</span> {t.submit}</button>
        </div>
      </fieldset>
      {!canEdit && <p className="text-muted mt-2">{fuText(locale, isHost ? 'historyLockedGm' : 'historyLockedPlayer')}</p>}
      {error && <Alert variant="danger" role="alert" className="mt-2 mb-0">
        {t[error.reason === 'json' ? 'jsonError' : error.reason === 'name' ? 'nameError' : error.reason]}
        {error.field && ` (${error.field})`}
      </Alert>}
      {sent && <p role="status" className="mt-2 mb-0">{fuText(locale, 'sent')}{sent}</p>}
    </Form>
  );
}
